import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import EventContext from "../EventContext";
import {getFullAddress, NullAsDefault, renderIf, renderIfElse} from "../../../Shared";
import {
    event_location_getCoordinateEventLocations_Api,
    event_location_getEventLocations_Api
} from "../../../../apis/event";
import {bindQueryParam} from "../../functions/routerHelper";
import useTranslate from "../../../../modules/local/useTranslate";
import {Avatar, Button, Image, Space} from "antd";
import {BiSolidChurch} from "react-icons/bi";
import {ItemInformation} from 'components/common/ItemInformation'
import _ from "lodash";
import {DoubleValueItem} from 'components/common/DoubleValueItem'
import {BsPinMapFill} from "react-icons/bs";
import {DetailViewForm} from "../ActionForms/EventLocationForm";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import EmptyHolder from "../../../../components/EmptyHolder";
import {useHistory} from "../../../../modules/navigation/useRouter";
import usePaginationHelper, {createPagerKey} from "../../hooks/usePaginationHelper";
import {useRouteSearchParam} from "../../../../envs/_current/history";
import {MdChurch} from "react-icons/md";
import {RiMapPinFill} from "react-icons/ri";
import LookupField from "../../../../components/LookupField";
import {useAppConfig} from "../../../../modules/local/AppConfigContext";
import {LeafletMap} from "../../../../components/form/fields/LeafletMap";
import Coordinates from "../../../../components/Coordinates/Coordinates";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";
import {getCoordinatesStore} from "../../../../helpers/localStorage";
import DrawDialog from "../../../../components/drawer/dialog/DrawDialog";
import {NoneStickyMeta} from "../../EntityList";
import Pure from "../../../../components/Pure";
import {nest} from "recompose";
import {Link} from "react-router-dom";
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import getTitle from "../../../../helpers/getTitle";
import {DetailModalContent} from "../../Church/components/ChurchForm";


const defaultFilter = {
    keyword: undefined,
    diocese_id: undefined,
}

const labels = {
    diocese_id: 'diocese',
};

export const EventLocationsFilter = ({
                                         value: filter = defaultFilter,
                                         onChange: setValues = NullAsDefault,
                                     }) => {
    const {dioceses = []} = useAppConfig('dioceses')
    const t = useTranslate()
    const history = useHistory()
    const {isLg} = useContext(LayoutContext)

    const handleChange = useCallback(
        (name, value) => {
            setValues({
                ...filter,
                [name]: value,
            })
        },
        [filter]
    )

    const filteredParams = Object.entries(filter).filter(([key, value]) => {
        switch (key) {
            case "keyword":
            // case "sortBy":
            default:
                return value !== undefined && value !== null;
        }
    }).map(([key, value]) => {
        switch (key) {
            case "diocese_id":
                const diocese = _.find(dioceses, {id: value})?.name
                return {
                    key,
                    value,
                    diocese: _.find(dioceses, {id: value}),
                    displayValue: `${t('diocese_short')} ${diocese}`,
                }
            case "keyword":
                return {
                    key,
                    value,
                    displayValue: `${t('keyword')}: ${value}`
                }
            case "sortBy":
                return {
                    key,
                    value,
                }
            default:
                return {
                    key,
                    value,
                    displayValue: value,
                    label: labels[key] || key,
                }
        }
    });


    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex flex-col gap-2">
                <span className="font-medium text-2xs text-gray-500 uppercase tracking-wider">
                    {t('diocese')}
                </span>
                <LookupField
                    title={t('choose diocese')}
                    placeholder={t('click to select')}
                    createIfNotExists={false}
                    defaultValue={_.find(filteredParams, {key: 'diocese_id'})?.displayValue}
                    selectedOption={_.find(filteredParams, {key: 'diocese_id'})?.diocese}
                    apiPath={`/lookup/diocese/get-by-types`}
                    OptionWrapper={({children, ...props}) =>
                        <div className="flex items-center gap-2 border border-color-50 hover:shadow-md rounded-lg relative" {...props}>
                            {children}
                        </div>
                    }
                    renderOption={(o, i) => {
                        const {fullname, location_address} = o ?? {}
                        return (
                            <div key={i} className="flex items-center flex-1 gap-3">
                                <Avatar
                                    size={50}
                                    src={o.avatar}
                                    className="flex flex-center background-200 rounded-l-lg rounded-r-0"
                                    icon={<MdChurch size={20} style={{color: '#dddddd'}}/>}
                                />
                                <div className="py-2 md:py-0 flex flex-col flex-1 min-w-0 gap-1">
                                    <div className="space-x-1 leading-snug">
                                    <span className="font-bold text-sm text-color-000">
                                        {fullname}
                                    </span>
                                    </div>
                                    {renderIf(
                                        location_address,
                                        <div className="flex items-center gap-x-1">
                                            <RiMapPinFill size={11} className="text-color-400"/>
                                            <span className="flex-1 max-lines-1 text-xs text-color-400 italic leading-snug">
                                            {location_address}
                                        </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    }}
                    handleChange={(e) => {
                        setValues({
                            diocese: e.target,
                            diocese_id: e.target.id,
                            diocese_name: e.target.name
                        })
                    }}
                />
            </div>
        </div>
    )
}

const EventLocations = ({
                            filter_actions = {
                                sortBy: 'sort-by',
                                keyword: 'keyword',
                                diocese_id: 'diocese',
                            },
                        }) => {
    const t = useTranslate()
    const history = useHistory()
    const {isLg} = useContext(LayoutContext)
    const {event} = useContext(EventContext)
    const [coordinates, setCoordinates] = useState([])
    const {latitude, longitude} = getCoordinatesStore();


    const {handleAsyncAction: getCoordinateEventLocations} = useAsyncAction({
        deps: [event?.id],
        apiInfo: event_location_getCoordinateEventLocations_Api,
        query: bindQueryParam({id: event?.id}),
        onSuccess: (result, data) => {
            const coordinates = _.get(data, 'response.data.data', []).map((e, i) => {
                return {
                    name: (
                        <div key={i}
                             className="flex gap-2 rounded-lg custom-item-render render-border-center hover:shadow relative">
                            <Avatar
                                size={66}
                                className="flex flex-center background-200 rounded-l-lg rounded-r-0 cursor-pointer"
                                src={e?.church?.photo}
                                icon={e?.church ? <BiSolidChurch size={60} className="text-color-500"/> :
                                    <BsPinMapFill/>}
                            />
                            <div className="flex flex-col flex-1 gap-1">
                                <div
                                    className="font-normal text-sm text-color-000 hover:text-primary max-lines-2 leading-tight cursor-pointer">
                                    {e?.name}
                                </div>
                                <div className="flex flex-col">
                                    {
                                        e?.church ? (
                                            <ItemInformation label="address" value={getFullAddress(e?.church)}
                                                             className="text-xs"/>
                                        ) : (
                                            <ItemInformation label="address" value={e?.address} className="text-xs"/>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                    ),
                    longitude: Number(e?.longitude),
                    latitude: Number(e?.latitude),
                }
            });
            setCoordinates(coordinates)
        },
    })

    const keyword = useRouteSearchParam(filter_actions.keyword)
    const diocese_id = useRouteSearchParam(filter_actions.diocese_id)
    const refresh_deps = useMemo(
        () => [
            diocese_id,
            latitude, longitude
        ],
        [diocese_id, latitude, longitude]
    )

    useEffect(() => {
        let values = {};
        if (keyword) values.keyword = keyword;
        if (diocese_id) values.diocese_id = diocese_id;
        getCoordinateEventLocations(values)
    }, [keyword, diocese_id])

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        isToggleDefault: false,
        AdvancedFilter: EventLocationsFilter,
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query: bindQueryParam({id: event?.id}),
            PaginationMeta: NoneStickyMeta,
            apiInfo: event_location_getEventLocations_Api,
            renderItem: (item, index) => {
                const {church, description, address, parish, diocese} = item ?? {}
                return (
                    <div key={index}
                         className="flex gap-3 rounded-lg custom-item-render render-border-center hover:shadow relative">
                        <Image
                            style={{
                                width: 120,
                                height: 120,
                                objectFit: 'cover',
                            }}
                            className="flex flex-center background-200 rounded-l-lg rounded-r-0 cursor-pointer"
                            src={church?.photo || 'images/photo_placeholder_location.jpg'}
                            fallback="images/photo_placeholder_location.jpg"
                            preview={false}
                        />
                        <div className="py-2 pr-2 flex flex-col flex-1 gap-1">
                            {
                                church ? (
                                    <Link to={getLinkToDetail(church)}
                                          className="mr-4 font-bold text-base text-color-000 hover:text-primary max-lines-2 leading-tight no-underline cursor-pointer">
                                        {getTitle(church)}
                                    </Link>
                                ) : (
                                    <DrawDialog
                                        width={600}
                                        title={
                                            <div
                                                className="mr-4 font-bold text-base text-color-000 hover:text-primary max-lines-2 leading-tight cursor-pointer">
                                                {item?.name}
                                            </div>
                                        }
                                        closable={false}
                                        component={
                                            <div
                                                className="mr-4 font-bold text-base text-color-000 hover:text-primary max-lines-2 leading-tight cursor-pointer">
                                                {item?.name}
                                            </div>
                                        }>
                                        {(isToggle, toggle) => isToggle ? (
                                            <DetailViewForm
                                                item={item}
                                            />
                                        ) : null}
                                    </DrawDialog>
                                )
                            }

                            <div className="flex flex-col">
                                {renderIfElse(
                                    church,
                                    <ItemInformation label="address" value={getFullAddress(church)}
                                                     className="text-xs"/>,
                                    <ItemInformation label="address" value={address} className="text-xs"/>
                                )}
                                {renderIfElse(
                                    church,
                                    <DoubleValueItem
                                        labelFirst="belong to parish"
                                        valueFirst={church?.parish_name}
                                        labelSecond={church?.diocese_name && " - " || ''}
                                        valueSecond={church?.diocese?.fullname}
                                        className="text-xs italic"
                                    />,
                                    <DoubleValueItem
                                        labelFirst="belong to parish"
                                        valueFirst={parish?.name}
                                        labelSecond={diocese?.fullname && " - " || ''}
                                        valueSecond={diocese?.fullname}
                                        className="text-xs italic"
                                    />
                                )}
                            </div>
                            {renderIf(
                                description,
                                <div className="text-xs text-color-300 max-lines-1">
                                    {description}
                                </div>
                            )}
                            <DrawDialog
                                width={600}
                                title={
                                    <div className="text-center font-bold text-base text-color-000 leading-snug no-underline max-lines-1">
                                        {item?.name}
                                    </div>
                                }
                                closable={false}
                                component={
                                    <span
                                        style={{marginTop: 'auto', textAlign: 'right', cursor: 'pointer'}}
                                        className="text-xs text-color-500 hover:text-color-100 italic tracking-wide no-underline">
                                                    {t('view detail')}</span>
                                }>
                                {(isToggle, toggle) => isToggle && (
                                    church ? (
                                        <DetailModalContent
                                            item={church}
                                            reportAccess={false}
                                        />
                                    ) : (
                                        <DetailViewForm
                                            item={item}
                                        />
                                    )
                                )}
                            </DrawDialog>
                        </div>
                    </div>
                )
            },
            renderEmpty: () => renderIfElse(
                event?.edit,
                <div
                    className="p-4 md:p-8 border border-color-50 hover:shadow rounded-lg flex flex-col items-center gap-2">
                    <div className="text-center font-semibold text-base text-color-100 tracking-wide">
                        {t('pilgrimage site')}
                    </div>
                    <div className="text-center text-sm text-color-300 italic tracking-wider leading-snug">
                        {t('pilgrimage site empty description')}
                    </div>
                    <span
                        onClick={() => (
                            history.push(`/event/${event?.idname}/settings/locations`)
                        )}
                        style={{padding: '4px 10px', borderRadius: '0.375rem'}}
                        className="mt-3 font-medium bg-primary-50 hover:bg-primary-100 text-xs text-primary-600 cursor-pointer tracking-wide">
                            {t('go to setting')}
                        </span>
                </div>,
                <EmptyHolder
                    icon={() => (
                        <BsPinMapFill size={40} className="text-color-500"/>
                    )}
                    title="pilgrimage site"
                    description="pilgrimage site empty description"
                />
            )
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({
                diocese_id,
            })
        },
        filter_params: {diocese_id
        },
        dependencies: [diocese_id],
    })

    return (
        <div className="flex flex-col gap-4">
            {coordinates?.length > 0 ? (
                <React.Fragment>
                    <LeafletMap
                        fullScreenAllow={true}
                        style={{
                            height: '50vh',
                        }}
                        zoom={12}
                        zoomControl={false}
                        coordinates={coordinates}
                        longitude={coordinates[0]?.longitude ?? 0}
                        latitude={coordinates[0]?.latitude ?? 0}
                    />
                    <div className="h-1"/>
                </React.Fragment>
            ) : null}

            {pager_widget}

        </div>
    )
}

export default nest(Pure, Coordinates, EventLocations)